import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Plus, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Not Started", "In Progress", "Completed", "On Hold", "Cancelled"];
const PRIORITIES = ["High", "Medium", "Low"];
const sBadge = (s) => ({ "Not Started": "bg-slate-100 text-slate-600", "In Progress": "bg-blue-100 text-blue-700", Completed: "bg-emerald-100 text-emerald-700", "On Hold": "bg-amber-100 text-amber-700", Cancelled: "bg-slate-200 text-slate-500" }[s] || "bg-slate-100");
const pBadge = (p) => ({ High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600" }[p] || "bg-slate-100");
const isOverdue = (t) => t.due_date && t.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10) && !["Completed", "Cancelled"].includes(t.status);

export default function TasksManager() {
  const { can, user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const matterFilter = sp.get("matter");
  const matterRef = sp.get("ref");
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", assignee: "", priority: "Medium", due_date: "", notes: "" });

  const load = useCallback(() => {
    const params = {};
    if (matterFilter) params.matter_id = matterFilter; else params.scope = scope;
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/admin/tasks", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [scope, status, q, matterFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const quickStatus = async (t, s) => { await api.put(`/admin/tasks/${t.id}`, { status: s }); load(); };
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/tasks", { ...form, assignee: form.assignee || user.email, ...(matterFilter ? { matter_id: matterFilter } : {}) }); toast.success("Task created"); setCreating(false); setForm({ title: "", assignee: "", priority: "Medium", due_date: "", notes: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="tasks-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Tasks</h1><p className="mt-1 text-sm text-slate-500">{items.length} task(s) · turn every matter into an actionable sequence.</p></div>
        {can("tasks", "create") && <button onClick={() => setCreating(true)} data-testid="new-task-btn" className="btn-gold text-sm"><Plus size={15} /> New task</button>}
      </div>

      {matterFilter && (<div data-testid="matter-filter-banner" className="mb-4 flex items-center justify-between rounded-lg border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm text-navy"><span>Showing tasks for matter <b>{matterRef || matterFilter}</b></span><button onClick={() => setSp({})} data-testid="clear-matter-filter" className="text-xs font-medium underline hover:no-underline">Clear filter</button></div>)}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 p-0.5">
          <button onClick={() => setScope("mine")} data-testid="tasks-scope-mine" className={`rounded-md px-3 py-1.5 text-sm font-medium ${scope === "mine" ? "bg-navy text-white" : "text-slate-500"}`}>My tasks</button>
          <button onClick={() => setScope("all")} data-testid="tasks-scope-team" className={`rounded-md px-3 py-1.5 text-sm font-medium ${scope === "all" ? "bg-navy text-white" : "text-slate-500"}`}>Team tasks</button>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="pl-9" data-testid="task-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="task-status-filter"><option value="all">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </div>

      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className={`flex flex-wrap items-center gap-3 rounded-xl border bg-white p-4 ${isOverdue(t) ? "border-red-200" : "border-slate-200"}`}>
            <button onClick={() => quickStatus(t, t.status === "Completed" ? "Not Started" : "Completed")} disabled={!can("tasks", "edit")} data-testid={`task-complete-${t.id}`} className="shrink-0">
              <CheckCircle2 size={20} className={t.status === "Completed" ? "text-emerald-600" : "text-slate-300 hover:text-slate-400"} />
            </button>
            <div className="min-w-[160px] flex-1">
              <div className={`font-medium text-navy ${t.status === "Completed" ? "line-through text-slate-400" : ""}`}>{t.title}</div>
              <div className="text-xs text-slate-400">{t.matter_ref ? `${t.matter_ref} · ` : ""}{t.assignee || "Unassigned"}</div>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${pBadge(t.priority)}`}>{t.priority}</span>
            {t.due_date && <span className={`flex items-center gap-1 text-xs ${isOverdue(t) ? "text-red-600 font-medium" : "text-slate-500"}`}><Clock size={13} /> {t.due_date.slice(0, 10)}</span>}
            <select value={t.status} onChange={(e) => quickStatus(t, e.target.value)} disabled={!can("tasks", "edit")} data-testid={`task-status-${t.id}`} className={`rounded-full px-2.5 py-1 text-xs font-medium ${sBadge(t.status)}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          </div>
        ))}
        {items.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">No tasks.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">New task</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Task</Label><Input data-testid="create-task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Assignee</Label><select data-testid="create-task-assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Me</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></div>
                <div><Label>Priority</Label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></div>
              </div>
              <div><Label>Due date</Label><Input type="date" data-testid="create-task-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
              <button type="submit" data-testid="create-task-submit" className="btn-gold w-full">Create task</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
